import React, { FC, useMemo } from 'react'
import { ApexOptions } from 'apexcharts'
import { Box, CircularProgress } from '@mui/material'
import { SensorBatteryStats } from 'src/api'
import { Chart } from 'src/components/ui'

interface Props {
  data: SensorBatteryStats[]
  isLoading?: boolean
}

const GROUP_LABELS = ['≤ 1 week', '2 weeks', '3-4 weeks', '2 months', '3 months', '> 3 months', 'n/a']

const groupDevicesByBatteryLife = (data: SensorBatteryStats[]): number[] => {
  const counts = [0, 0, 0, 0, 0, 0, 0]

  for (const item of data) {
    const days = (item.daysSinceLastCharge ?? 0) + (item.estimatedDaysRemaining ?? 0)

    if (!item.dailyDischargeRate || !item.estimatedDaysRemaining) {
      counts[6]++
    } else if (days <= 7) {
      counts[0]++
    } else if (days <= 14) {
      counts[1]++
    } else if (days <= 30) {
      counts[2]++
    } else if (days <= 60) {
      counts[3]++
    } else if (days <= 90) {
      counts[4]++
    } else {
      counts[5]++
    }
  }

  return counts
}

export const BatteryLifeDistributionChart: FC<Props> = ({ data, isLoading = false }) => {
  const groupCounts = useMemo(() => groupDevicesByBatteryLife(data), [data])

  const series: ApexOptions['series'] = useMemo(
    () => [
      {
        name: 'Devices',
        data: groupCounts
      }
    ],
    [groupCounts]
  )

  const chartOptions: ApexOptions = {
    chart: {
      type: 'bar',
      toolbar: {
        tools: {
          download: true,
          selection: false,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false
        }
      }
    },
    plotOptions: {
      bar: {
        horizontal: true,
        borderRadius: 4
      }
    },
    dataLabels: {
      enabled: true
    },
    xaxis: {
      categories: GROUP_LABELS,
      title: {
        text: 'Number of devices'
      }
    },
    tooltip: {
      y: {
        formatter: (val) => `${val} devices`
      }
    }
  }

  return (
    <Box height="350px" width="100%" position="relative">
      {isLoading && (
        <>
          <Box
            position="absolute"
            width="100%"
            height="100%"
            zIndex={1}
            sx={{
              background: 'grey',
              opacity: 0.1
            }}
          />
          <Box
            position="absolute"
            width="100%"
            height="100%"
            display="flex"
            justifyContent="center"
            alignItems="center"
            zIndex={2}
          >
            <CircularProgress color="primary" size={26} />
          </Box>
        </>
      )}
      <Chart options={chartOptions} data={series} />
    </Box>
  )
}
